import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Grid,
  Divider,
  Tab,
  Tabs,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Search as SearchIcon,
  ImageNotSupported as NoImageIcon,
  OpenInNew as OpenInNewIcon,
  LocalCafeOutlined as PreferenceIcon,
  DescriptionOutlined as ClaimIcon,
  MenuBookOutlined as SourceTextIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../../services/firebase";
import { algoliaClient, ALGOLIA_INDEX_NAME } from "../../services/algolia";
import {
  WeekOption,
  WeeklyTopicsWithDetails,
  Topic,
  QuestionType,
  GENRES,
  GENRE_LABELS,
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  Genre,
} from "../../types";
import DifficultyChip from "../common/DifficultyChip";
import GenreChip from "../common/GenreChip";
import TopicalityChip from "../common/TopicalityChip";

// Algolia検索結果の型
interface AlgoliaTopic {
  objectID: string;
  theme: string;
  sourceText?: string;
  genre: string;
  difficulty: number;
  imageUrl?: string;
  questionType?: string;
  claimType?: string;
  background?: string;
}

// お題のカテゴリ（ホーム画面の並びと同じ考え方）
type TopicCategory = "preference" | "claim" | "claimWithSourceText";
const hasSourceText = (t: Topic): boolean => Boolean((t.sourceText ?? "").trim());
const categoryOf = (t: Topic): TopicCategory =>
  hasSourceText(t)
    ? "claimWithSourceText"
    : t.questionType === "preference"
      ? "preference"
      : "claim";

// === ヘルパー関数（週ID計算用） ===

/**
 * ISO週番号を計算する（月曜日始まり）
 */
const getISOWeekNumber = (date: Date): { year: number; week: number } => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNumber };
};

/**
 * 週IDを生成（例: "2024-W03"）
 */
const generateWeekId = (date: Date): string => {
  const { year, week } = getISOWeekNumber(date);
  return `${year}-W${week.toString().padStart(2, "0")}`;
};

/**
 * 週の開始日（月曜日）を取得
 */
const getWeekStartDate = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * 週の終了日（日曜日）を取得
 */
const getWeekEndDate = (date: Date): Date => {
  const startDate = getWeekStartDate(date);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);
  return endDate;
};

/**
 * 週ラベルのフォーマット
 */
const formatWeekLabel = (startDate: Date, endDate: Date): string => {
  const { year, week } = getISOWeekNumber(startDate);
  const startMonth = startDate.getMonth() + 1;
  const startDay = startDate.getDate();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();
  return `${year}年 第${week}週 (${startMonth}/${startDay}-${endMonth}/${endDay})`;
};

// === Firestore 直接アクセス関数 ===

/**
 * 週リスト取得（Firestore直接）
 */
const fetchWeekOptionsFromFirestore = async (
  weeksAhead: number = 8,
  weeksBehind: number = 24
): Promise<{ weeks: WeekOption[] }> => {
  const weeks: WeekOption[] = [];
  const today = new Date();
  const currentWeekStart = getWeekStartDate(today);

  // 過去の週
  for (let i = weeksBehind; i > 0; i--) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() - i * 7);
    const weekEnd = getWeekEndDate(weekStart);
    const weekId = generateWeekId(weekStart);

    weeks.push({
      weekId,
      weekStartDate: weekStart.toISOString(),
      weekEndDate: weekEnd.toISOString(),
      label: formatWeekLabel(weekStart, weekEnd),
      hasData: false,
    });
  }

  // 今週
  const currentWeekEnd = getWeekEndDate(currentWeekStart);
  const currentWeekId = generateWeekId(currentWeekStart);
  weeks.push({
    weekId: currentWeekId,
    weekStartDate: currentWeekStart.toISOString(),
    weekEndDate: currentWeekEnd.toISOString(),
    label: formatWeekLabel(currentWeekStart, currentWeekEnd) + "（今週）",
    hasData: false,
  });

  // 未来の週
  for (let i = 1; i <= weeksAhead; i++) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() + i * 7);
    const weekEnd = getWeekEndDate(weekStart);
    const weekId = generateWeekId(weekStart);

    weeks.push({
      weekId,
      weekStartDate: weekStart.toISOString(),
      weekEndDate: weekEnd.toISOString(),
      label: formatWeekLabel(weekStart, weekEnd),
      hasData: false,
    });
  }

  // 既存データの確認
  const existingDocs = await Promise.all(
    weeks.map((w) => getDoc(doc(db, "weeklyTopics", w.weekId)))
  );

  existingDocs.forEach((docSnap, index) => {
    weeks[index].hasData = docSnap.exists();
  });

  return { weeks };
};

/**
 * 週詳細取得（Firestore直接）
 */
const fetchWeeklyTopicsByIdFromFirestore = async (
  weekId: string
): Promise<WeeklyTopicsWithDetails> => {
  const docRef = doc(db, "weeklyTopics", weekId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error("Weekly topics not found");
  }

  const data = docSnap.data();
  const topicIds = (data.topicIds || []) as string[];

  // topicの詳細情報を取得
  const topics: Topic[] = [];
  if (topicIds.length > 0) {
    const opinionDocs = await Promise.all(topicIds.map((id) => getDoc(doc(db, "topics", id))));

    for (const opinionDoc of opinionDocs) {
      if (opinionDoc.exists()) {
        const opinionData = opinionDoc.data();
        topics.push({
          id: opinionDoc.id,
          difficulty: opinionData.difficulty,
          genre: opinionData.genre,
          theme: opinionData.theme,
          sourceText: opinionData.sourceText,
          opinionPatterns: opinionData.opinionPatterns || [],
          isPublished: opinionData.isPublished,
          isTopical: opinionData.isTopical,
          imageUrl: opinionData.imageUrl || null,
          questionType: opinionData.questionType,
          claimType: opinionData.claimType,
          background: opinionData.background,
          xPostUrl: opinionData.xPostUrl,
        } as Topic);
      }
    }
  }

  const weekStartDate = data.weekStartDate as Timestamp | null;
  const weekEndDate = data.weekEndDate as Timestamp | null;
  const createdAt = data.createdAt as Timestamp | null;
  const updatedAt = data.updatedAt as Timestamp | null;

  return {
    id: docSnap.id,
    weekStartDate: weekStartDate?.toDate?.().toISOString() || "",
    weekEndDate: weekEndDate?.toDate?.().toISOString() || "",
    topicIds,
    topics,
    createdAt: createdAt?.toDate?.().toISOString() || "",
    updatedAt: updatedAt?.toDate?.().toISOString() || "",
    createdBy: data.createdBy || "",
  };
};

/**
 * 週間問題作成（Firestore直接）
 */
const createWeeklyTopicsInFirestore = async (data: {
  weekStartDate: string;
  topicIds: string[];
}): Promise<{ id: string }> => {
  const startDate = new Date(data.weekStartDate);
  const weekId = generateWeekId(startDate);

  // 既存チェック
  const existingDoc = await getDoc(doc(db, "weeklyTopics", weekId));
  if (existingDoc.exists()) {
    throw new Error("Weekly topics already exist for this week");
  }

  const weeklyTopics = {
    weekStartDate: Timestamp.fromDate(getWeekStartDate(startDate)),
    weekEndDate: Timestamp.fromDate(getWeekEndDate(startDate)),
    topicIds: data.topicIds,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: auth.currentUser?.email || auth.currentUser?.uid || "unknown",
  };

  await setDoc(doc(db, "weeklyTopics", weekId), weeklyTopics);
  return { id: weekId };
};

/**
 * 週間問題更新（Firestore直接）
 */
const updateWeeklyTopicsInFirestore = async (
  weekId: string,
  data: { topicIds: string[] }
): Promise<{ id: string; topicIds: string[]; updated: boolean }> => {
  const docRef = doc(db, "weeklyTopics", weekId);
  await updateDoc(docRef, {
    topicIds: data.topicIds,
    updatedAt: serverTimestamp(),
  });
  return { id: weekId, topicIds: data.topicIds, updated: true };
};

/**
 * 週間問題削除（Firestore直接）
 */
const deleteWeeklyTopicsFromFirestore = async (
  weekId: string
): Promise<{ id: string; deleted: boolean }> => {
  await deleteDoc(doc(db, "weeklyTopics", weekId));
  return { id: weekId, deleted: true };
};

const WeeklyTopicsSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedWeek, setSelectedWeek] = useState<WeekOption | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [weekListTab, setWeekListTab] = useState<"upcoming" | "past">("upcoming");

  // 週リスト取得（Firestore直接）
  const {
    data: weekOptionsData,
    isLoading: isLoadingWeeks,
    error: weeksError,
  } = useQuery("weekOptions", () => fetchWeekOptionsFromFirestore(8, 24), {
    staleTime: 1000 * 60 * 5, // 5分
  });

  // 選択した週の詳細取得（Firestore直接）
  const {
    data: weekDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
  } = useQuery(
    ["weeklyTopicsDetails", selectedWeek?.weekId],
    () => fetchWeeklyTopicsByIdFromFirestore(selectedWeek!.weekId),
    {
      enabled: !!selectedWeek?.hasData,
      staleTime: 1000 * 60 * 5,
    }
  );

  const handleWeekSelect = (week: WeekOption) => {
    setSelectedWeek(week);
  };

  const handleCreateOrEdit = () => {
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const deleteMutation = useMutation(() => deleteWeeklyTopicsFromFirestore(selectedWeek!.weekId), {
    onSuccess: () => {
      queryClient.invalidateQueries("weekOptions");
      queryClient.invalidateQueries(["weeklyTopicsDetails", selectedWeek?.weekId]);
      setSelectedWeek((prev) => (prev ? { ...prev, hasData: false } : null));
      setDeleteDialogOpen(false);
    },
  });

  if (isLoadingWeeks) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (weeksError) {
    return <Alert severity="error">週リストの取得に失敗しました</Alert>;
  }

  const weeks = weekOptionsData?.weeks || [];
  const currentWeekId = generateWeekId(new Date());
  const upcomingWeeks = weeks.filter((w) => w.weekId >= currentWeekId);
  const pastWeeks = [...weeks.filter((w) => w.weekId < currentWeekId)].reverse();
  const displayedWeeks = weekListTab === "upcoming" ? upcomingWeeks : pastWeeks;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        週間問題設定
      </Typography>

      <Grid container spacing={3}>
        {/* 週リスト */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              週を選択
            </Typography>
            <Tabs value={weekListTab} onChange={(_, value) => setWeekListTab(value)} sx={{ mb: 1 }}>
              <Tab label="今週以降" value="upcoming" />
              <Tab label="過去" value="past" />
            </Tabs>
            <List sx={{ maxHeight: 450, overflow: "auto" }}>
              {displayedWeeks.map((week) => (
                <ListItem key={week.weekId} disablePadding>
                  <ListItemButton
                    selected={selectedWeek?.weekId === week.weekId}
                    onClick={() => handleWeekSelect(week)}
                  >
                    <ListItemIcon>
                      {week.hasData ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CalendarIcon color="disabled" />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={week.label} secondary={week.weekId} />
                    {week.hasData && (
                      <Chip label="設定済" size="small" color="success" variant="outlined" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* 詳細表示 */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            {!selectedWeek ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={300}
              >
                <CalendarIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography color="text.secondary">左のリストから週を選択してください</Typography>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h5">{selectedWeek.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedWeek.weekId}
                    </Typography>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={selectedWeek.hasData ? <EditIcon /> : <AddIcon />}
                      onClick={handleCreateOrEdit}
                    >
                      {selectedWeek.hasData ? "編集" : "設定"}
                    </Button>
                    {selectedWeek.hasData && (
                      <IconButton color="error" onClick={handleDeleteClick} sx={{ ml: 1 }}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {!selectedWeek.hasData ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={200}
                  >
                    <AutoIcon sx={{ fontSize: 48, color: "warning.main", mb: 2 }} />
                    <Typography color="text.secondary" align="center">
                      この週の問題は未設定です
                      {/* 現在未使用のためコメントアウト */}
                      {/* <br /> */}
                      {/* 未設定のまま週が近づくと自動生成されます */}
                    </Typography>
                  </Box>
                ) : isLoadingDetails ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : detailsError ? (
                  <Alert severity="error">詳細の取得に失敗しました</Alert>
                ) : weekDetails ? (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        作成者: {weekDetails.createdBy}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle1" gutterBottom>
                      設定された問題 ({weekDetails.topics.length}件)
                    </Typography>
                    {(
                      [
                        {
                          cat: "preference",
                          label: "思いを伝える",
                          icon: <PreferenceIcon fontSize="small" />,
                        },
                        {
                          cat: "claim",
                          label: "主張を伝える",
                          icon: <ClaimIcon fontSize="small" />,
                        },
                        {
                          cat: "claimWithSourceText",
                          label: "主張を伝える（課題文あり）",
                          icon: <SourceTextIcon fontSize="small" />,
                        },
                      ] as const
                    ).map(({ cat, label, icon }) => {
                      const topicsOfType = weekDetails.topics.filter((t) => categoryOf(t) === cat);
                      if (topicsOfType.length === 0) return null;
                      return (
                        <Box key={cat} sx={{ mb: 2 }}>
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                            {icon}
                            <Typography variant="subtitle2" color="text.secondary">
                              {label}（{topicsOfType.length}件）
                            </Typography>
                          </Box>
                          <List dense>
                            {topicsOfType.map((opinion) => (
                              <ListItem
                                key={opinion.id}
                                sx={{ py: 1 }}
                                secondaryAction={
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    component="a"
                                    href={`/opinions/edit/${opinion.id}`}
                                    target="_blank"
                                    title="問題を編集"
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                }
                              >
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1}>
                                      {opinion.imageUrl ? (
                                        <Box
                                          component="img"
                                          src={opinion.imageUrl}
                                          alt="問題画像"
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            objectFit: "cover",
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                          }}
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "grey.100",
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                          }}
                                        >
                                          <NoImageIcon fontSize="small" color="disabled" />
                                        </Box>
                                      )}
                                      <Typography variant="body1">
                                        {opinion.theme ||
                                          (opinion.sourceText || "").substring(0, 50)}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                      <DifficultyChip
                                        difficulty={opinion.difficulty}
                                        size="small"
                                      />
                                      <GenreChip genre={opinion.genre} size="small" />
                                      <TopicalityChip isTopical={opinion.isTopical} />
                                      {opinion.isPublished ? (
                                        <Chip
                                          label="公開中"
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      ) : (
                                        <Chip label="非公開" size="small" color="error" />
                                      )}
                                      {opinion.xPostUrl ? (
                                        <Chip
                                          label="X投稿済"
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          clickable
                                          component="a"
                                          href={opinion.xPostUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        />
                                      ) : (
                                        <Chip
                                          label="X未投稿"
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 編集ダイアログ */}
      {selectedWeek && (
        <WeeklyTopicsEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          week={selectedWeek}
          existingData={selectedWeek.hasData ? weekDetails : undefined}
        />
      )}

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>週間問題設定の削除</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedWeek?.label}の問題設定を削除しますか？
            <br />
            削除後、この週は自動生成の対象になります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? <CircularProgress size={20} /> : "削除"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// 編集ダイアログ
interface WeeklyTopicsEditDialogProps {
  open: boolean;
  onClose: () => void;
  week: WeekOption;
  existingData?: WeeklyTopicsWithDetails;
}

const WeeklyTopicsEditDialog: React.FC<WeeklyTopicsEditDialogProps> = ({
  open,
  onClose,
  week,
  existingData,
}) => {
  const queryClient = useQueryClient();
  const [selectedOpinions, setSelectedOpinions] = useState<Topic[]>(existingData?.topics || []);
  const [selectedTypeTab, setSelectedTypeTab] = useState<TopicCategory>("preference");
  const [searchInput, setSearchInput] = useState("");
  const [genreFilter, setGenreFilter] = useState<Genre | "">("");
  const [difficultyFilter, setDifficultyFilter] = useState<string>("");
  const [searchResults, setSearchResults] = useState<Topic[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // 直近実行した検索の文字クエリ（文字検索時は出題済みトピックの除外を解除するために保持）
  const [searchedQuery, setSearchedQuery] = useState("");

  // 初期データ取得（更新日時の新しい順で最新100件）- Firestore直接アクセス
  const { data: initialOpinions, isLoading: isLoadingInitial } = useQuery(
    ["recentApprovedOpinions"],
    async () => {
      const q = query(collection(db, "topics"), orderBy("updatedAt", "desc"), limit(100));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Topic[];
    },
    {
      enabled: open,
      staleTime: 1000 * 60 * 5,
    }
  );

  // 候補から除外するtopicIdを取得
  // - 過去4週間（今週含む）に出題したトピック（直近重複を避ける）
  // - 今週以降（未来含む）に既に設定済みのトピック（来週・再来週などへの二重設定を避ける）
  const { data: assignedTopicIds } = useQuery(
    ["assignedTopicIds"],
    async () => {
      const currentWeekStart = getWeekStartDate(new Date());
      const ids = new Set<string>();

      // 過去4週間（今週含む）: weekId 直接参照
      const pastWeekIds: string[] = [];
      for (let i = 0; i < 4; i++) {
        const weekStart = new Date(currentWeekStart);
        weekStart.setDate(currentWeekStart.getDate() - i * 7);
        pastWeekIds.push(generateWeekId(weekStart));
      }
      const pastDocs = await Promise.all(
        pastWeekIds.map((weekId) => getDoc(doc(db, "weeklyTopics", weekId)))
      );
      pastDocs.forEach((d) => {
        if (d.exists()) {
          ((d.data().topicIds || []) as string[]).forEach((id) => ids.add(id));
        }
      });

      // 今週以降（未来含む）に設定済みの全週: weekStartDate で範囲取得
      const futureSnapshot = await getDocs(
        query(
          collection(db, "weeklyTopics"),
          where("weekStartDate", ">=", Timestamp.fromDate(currentWeekStart))
        )
      );
      futureSnapshot.forEach((d) => {
        ((d.data().topicIds || []) as string[]).forEach((id) => ids.add(id));
      });

      return ids;
    },
    {
      enabled: open,
      staleTime: 1000 * 60 * 5,
    }
  );

  // 検索実行 - Algoliaで全文検索
  const handleSearch = useCallback(async () => {
    const trimmedQuery = searchInput.trim();
    // 検索ワード・フィルタすべて未指定なら検索状態を解除
    if (!trimmedQuery && !genreFilter && difficultyFilter === "") {
      setSearchResults([]);
      setHasSearched(false);
      setSearchedQuery("");
      return;
    }

    setIsSearching(true);
    try {
      // フィルタ条件を組み立て
      const filterClauses: string[] = [];
      if (genreFilter) filterClauses.push(`genre:${genreFilter}`);
      if (difficultyFilter !== "") filterClauses.push(`difficulty:${difficultyFilter}`);
      const filters = filterClauses.join(" AND ");

      // Algoliaで検索（isPublished=trueのみがインデックスされている）
      const { results } = await algoliaClient.search<AlgoliaTopic>({
        requests: [
          {
            indexName: ALGOLIA_INDEX_NAME,
            query: trimmedQuery,
            hitsPerPage: 50,
            ...(filters ? { filters } : {}),
          },
        ],
      });

      // 検索結果をTopic形式に変換
      const searchResultHits = results[0];
      if ("hits" in searchResultHits) {
        const opinions: Topic[] = searchResultHits.hits.map((hit) => ({
          id: hit.objectID,
          theme: hit.theme || "",
          sourceText: hit.sourceText,
          genre: hit.genre || "",
          difficulty: hit.difficulty || 1,
          imageUrl: hit.imageUrl,
          questionType: hit.questionType as QuestionType | undefined,
          claimType: hit.claimType as Topic["claimType"],
          background: hit.background,
          // 以下は検索結果に含まれないため、デフォルト値を設定
          opinionPatterns: [],
          isPublished: true,
          version: 1,
          createdAt: "",
          updatedAt: "",
          createdBy: "",
          tags: [],
        }));
        setSearchResults(opinions);
      } else {
        setSearchResults([]);
      }
      setHasSearched(true);
      setSearchedQuery(trimmedQuery);
    } catch (error) {
      console.error("Algolia search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchInput, genreFilter, difficultyFilter]);

  // Enterキーで検索
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 既存データが変更されたら反映
  React.useEffect(() => {
    console.log(
      "useEffect発火: existingData変更",
      existingData?.topics?.map((o) => o.id)
    );
    if (existingData?.topics) {
      setSelectedOpinions(existingData.topics);
    } else {
      setSelectedOpinions([]);
    }
  }, [existingData]);

  const createMutation = useMutation(
    (data: { weekStartDate: string; topicIds: string[] }) => createWeeklyTopicsInFirestore(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("weekOptions");
        queryClient.invalidateQueries(["weeklyTopicsDetails", week.weekId]);
        onClose();
      },
      onError: (error: unknown) => {
        console.error("週間問題の作成に失敗しました:", error);
        if (error instanceof Error) {
          console.error("エラーメッセージ:", error.message);
          console.error("スタックトレース:", error.stack);
        }
      },
    }
  );

  const updateMutation = useMutation(
    (data: { topicIds: string[] }) => updateWeeklyTopicsInFirestore(week.weekId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("weekOptions");
        queryClient.invalidateQueries(["weeklyTopicsDetails", week.weekId]);
        onClose();
      },
      onError: (error: unknown) => {
        console.error("週間問題の更新に失敗しました:", error);
        if (error instanceof Error) {
          console.error("エラーメッセージ:", error.message);
          console.error("スタックトレース:", error.stack);
        }
      },
    }
  );

  const handleSave = () => {
    console.log("selectedOpinions:", selectedOpinions);
    console.log(
      "selectedOpinions詳細:",
      selectedOpinions.map((o) => ({ id: o.id, theme: o.theme }))
    );
    const topicIds = selectedOpinions.map((o) => o.id!);

    console.log("保存データ:", {
      weekId: week.weekId,
      weekStartDate: week.weekStartDate,
      topicIds,
      isUpdate: !!existingData,
    });

    if (existingData) {
      updateMutation.mutate({ topicIds });
    } else {
      createMutation.mutate({
        weekStartDate: week.weekStartDate,
        topicIds,
      });
    }
  };

  const handleAddOpinion = (opinion: Topic | null) => {
    if (opinion) {
      console.log("追加する問題:", { id: opinion.id, theme: opinion.theme });
      if (!selectedOpinions.find((o) => o.id === opinion.id)) {
        const newList = [...selectedOpinions, opinion];
        console.log(
          "追加後のリスト:",
          newList.map((o) => ({ id: o.id, theme: o.theme }))
        );
        setSelectedOpinions(newList);
      }
    }
  };

  const handleRemoveOpinion = (opinionId: string) => {
    setSelectedOpinions(selectedOpinions.filter((o) => o.id !== opinionId));
  };

  const handleMoveOpinion = (opinionId: string, direction: "up" | "down") => {
    // アクティブなカテゴリ内で並び替え、カテゴリ順でマージし直す
    const activeCat = selectedTypeTab;
    const inCat = selectedOpinions.filter((t) => categoryOf(t) === activeCat);
    const index = inCat.findIndex((t) => t.id === opinionId);
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= inCat.length) return;
    [inCat[index], inCat[targetIndex]] = [inCat[targetIndex], inCat[index]];
    // 思いを伝える → 主張を伝える → 課題文あり の順でマージ
    const cats: TopicCategory[] = ["preference", "claim", "claimWithSourceText"];
    const merged = cats.flatMap((c) =>
      c === activeCat ? inCat : selectedOpinions.filter((t) => categoryOf(t) === c)
    );
    setSelectedOpinions(merged);
  };

  // 表示する候補（検索結果があればそれ、なければ初期100件）
  // 除外: 選択済み / 過去4週間に出題したトピック / 今週以降（未来含む）に設定済みのトピック
  // ただし文字で検索したときは、意図して探しているとみなし出題済みトピックも表示する
  const availableOpinions = useMemo(() => {
    const selectedIds = new Set(selectedOpinions.map((o) => o.id));
    const excludedIds = assignedTopicIds || new Set<string>();
    const sourceList = hasSearched ? searchResults : initialOpinions || [];
    const isTextSearch = hasSearched && searchedQuery !== "";
    return sourceList.filter((o) => {
      if (selectedIds.has(o.id)) return false;
      if (!isTextSearch && excludedIds.has(o.id!)) return false;
      // 検索未実行時はクライアント側でフィルタ適用（Algolia検索済みなら既に絞られている）
      if (!hasSearched) {
        if (genreFilter && o.genre !== genreFilter) return false;
        if (difficultyFilter !== "" && o.difficulty !== Number(difficultyFilter)) return false;
      }
      return true;
    });
  }, [
    initialOpinions,
    searchResults,
    selectedOpinions,
    assignedTopicIds,
    hasSearched,
    searchedQuery,
    genreFilter,
    difficultyFilter,
  ]);

  const isValid = selectedOpinions.length >= 1;
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {existingData ? "週間問題の編集" : "週間問題の設定"}
        <Typography variant="body2" color="text.secondary">
          {week.label}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            選択した問題 ({selectedOpinions.length}件)
          </Typography>

          <Tabs
            value={selectedTypeTab}
            onChange={(_e, value) => setSelectedTypeTab(value)}
            sx={{ mb: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              value="preference"
              icon={<PreferenceIcon fontSize="small" />}
              iconPosition="start"
              label={`思いを伝える (${selectedOpinions.filter((o) => categoryOf(o) === "preference").length})`}
              sx={{ textTransform: "none", minHeight: 40 }}
            />
            <Tab
              value="claim"
              icon={<ClaimIcon fontSize="small" />}
              iconPosition="start"
              label={`主張を伝える (${selectedOpinions.filter((o) => categoryOf(o) === "claim").length})`}
              sx={{ textTransform: "none", minHeight: 40 }}
            />
            <Tab
              value="claimWithSourceText"
              icon={<SourceTextIcon fontSize="small" />}
              iconPosition="start"
              label={`課題文あり (${selectedOpinions.filter((o) => categoryOf(o) === "claimWithSourceText").length})`}
              sx={{ textTransform: "none", minHeight: 40 }}
            />
          </Tabs>

          {(() => {
            const filteredOpinions = selectedOpinions.filter(
              (t) => categoryOf(t) === selectedTypeTab
            );
            const categoryLabels: Record<TopicCategory, string> = {
              preference: "思いを伝える",
              claim: "主張を伝える",
              claimWithSourceText: "主張を伝える（課題文あり）",
            };

            if (filteredOpinions.length === 0) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  「{categoryLabels[selectedTypeTab]}」の問題が選択されていません
                </Alert>
              );
            }

            return (
              <List sx={{ mb: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                {filteredOpinions.map((opinion, index) => (
                  <ListItem
                    key={opinion.id}
                    sx={{
                      bgcolor: "background.paper",
                      mb: 0.5,
                      borderRadius: 1,
                    }}
                    secondaryAction={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <IconButton
                          size="small"
                          color="primary"
                          component="a"
                          href={`/opinions/edit/${opinion.id}`}
                          target="_blank"
                          title="問題を編集"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveOpinion(opinion.id!, "up")}
                          disabled={index === 0}
                          title="上に移動"
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveOpinion(opinion.id!, "down")}
                          disabled={index === filteredOpinions.length - 1}
                          title="下に移動"
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveOpinion(opinion.id!)}
                          title="削除"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip label={`${index + 1}`} size="small" color="primary" />
                          {opinion.imageUrl ? (
                            <Box
                              component="img"
                              src={opinion.imageUrl}
                              alt="問題画像"
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "grey.100",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <NoImageIcon sx={{ fontSize: 16 }} color="disabled" />
                            </Box>
                          )}
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {opinion.theme || (opinion.sourceText || "").substring(0, 50)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <DifficultyChip difficulty={opinion.difficulty} size="small" />
                          <GenreChip genre={opinion.genre} size="small" />
                          {/* Algolia検索結果はisPublishedが取れずtrue固定のため、falseが確定しているときだけ警告表示 */}
                          {opinion.isPublished === false && (
                            <Chip label="非公開" size="small" color="error" />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            );
          })()}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            問題を追加
          </Typography>

          {/* 検索欄 */}
          <Box display="flex" gap={1} mb={1}>
            <TextField
              fullWidth
              size="small"
              label="問題を検索"
              placeholder="テーマや内容で検索..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <Button
              variant="outlined"
              onClick={handleSearch}
              disabled={isSearching}
              startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
              sx={{ minWidth: 100 }}
            >
              検索
            </Button>
          </Box>

          {/* カテゴリ・難易度フィルタ */}
          <Box display="flex" gap={1} mb={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="genre-filter-label">カテゴリ</InputLabel>
              <Select
                labelId="genre-filter-label"
                label="カテゴリ"
                value={genreFilter}
                onChange={(e) => setGenreFilter(e.target.value as Genre | "")}
              >
                <MenuItem value="">
                  <em>すべて</em>
                </MenuItem>
                {GENRES.map((genre) => (
                  <MenuItem key={genre} value={genre}>
                    {GENRE_LABELS[genre]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="difficulty-filter-label">難易度</InputLabel>
              <Select
                labelId="difficulty-filter-label"
                label="難易度"
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>すべて</em>
                </MenuItem>
                {DIFFICULTIES.map((difficulty) => (
                  <MenuItem key={difficulty} value={String(difficulty)}>
                    {DIFFICULTY_LABELS[difficulty]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(genreFilter || difficultyFilter !== "") && (
              <Button
                size="small"
                onClick={() => {
                  setGenreFilter("");
                  setDifficultyFilter("");
                }}
              >
                クリア
              </Button>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            {hasSearched
              ? `検索結果（直近4週間に出題したものは除外）: ${availableOpinions.length}件`
              : `問題（更新順100件・直近4週間に出題したものは除外）: ${availableOpinions.length}件`}
          </Typography>

          {/* 候補リスト */}
          {isLoadingInitial ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : availableOpinions.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {hasSearched ? "検索結果がありません" : "問題がありません"}
            </Alert>
          ) : (
            <List sx={{ maxHeight: 300, overflow: "auto", bgcolor: "grey.50", borderRadius: 1 }}>
              {availableOpinions.map((opinion) => (
                <ListItem key={opinion.id} disablePadding>
                  <ListItemButton onClick={() => handleAddOpinion(opinion)}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {opinion.imageUrl ? (
                            <Box
                              component="img"
                              src={opinion.imageUrl}
                              alt="問題画像"
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "grey.200",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <NoImageIcon sx={{ fontSize: 16 }} color="disabled" />
                            </Box>
                          )}
                          <DifficultyChip difficulty={opinion.difficulty} size="small" />
                          <GenreChip genre={opinion.genre} size="small" />
                          {opinion.questionType && (
                            <Chip
                              icon={
                                opinion.questionType === "preference" ? (
                                  <PreferenceIcon />
                                ) : (
                                  <ClaimIcon />
                                )
                              }
                              label={
                                opinion.questionType === "preference"
                                  ? "思いを伝える"
                                  : "主張を伝える"
                              }
                              size="small"
                              variant="outlined"
                              color={opinion.questionType === "preference" ? "error" : "primary"}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {opinion.theme || (opinion.sourceText || "").substring(0, 50)}
                        </Typography>
                      }
                    />
                    <AddIcon color="primary" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {(createMutation.isError || updateMutation.isError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              保存に失敗しました。もう一度お試しください。
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid || isSaving}>
          {isSaving ? <CircularProgress size={20} /> : "保存"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeeklyTopicsSettings;
